import { AuthBindings } from "@refinedev/core";
import { axiosInstance } from "./dataProvider";
import { getDeviceFingerprint } from "../utils/deviceFingerprint";

export const authProvider: AuthBindings = {
  login: async ({
    username,
    password,
    mfaChallengeToken,
    mfaCode,
    trustDevice,
  }: {
    username?: string;
    password?: string;
    mfaChallengeToken?: string;
    mfaCode?: string;
    trustDevice?: boolean;
  }) => {
    const deviceFingerprint = getDeviceFingerprint();

    if (mfaChallengeToken) {
      // Step 2: MFA verification — let errors propagate so Login.tsx onError handles 410/429
      const { data } = await axiosInstance.post("auth/mfa/verify", {
        challengeToken: mfaChallengeToken,
        code: mfaCode,
        trustDevice: trustDevice ?? false,
        deviceFingerprint,
      });

      localStorage.setItem("access_token", data.accessToken);
      localStorage.setItem("refresh_token", data.refreshToken);
      localStorage.setItem("user", JSON.stringify(data.user));

      return { success: true, redirectTo: "/" };
    }

    // Step 1: Password authentication — catch errors to show generic message
    try {
      const { data } = await axiosInstance.post("auth/login", {
        username,
        password,
        deviceFingerprint,
      });

      // MFA challenge required — return to Login.tsx for OTP step
      if (data.mfaRequired) {
        return {
          success: false,
          error: {
            name: "mfa_required",
            message: data.challengeToken,
          },
        };
      }

      // Store tokens
      localStorage.setItem("access_token", data.accessToken);
      localStorage.setItem("refresh_token", data.refreshToken);
      localStorage.setItem("user", JSON.stringify(data.user));

      // MFA enrollment enforcement
      if (data.mfaEnrollmentRequired) {
        localStorage.setItem("mfa_enrollment_required", "true");
        return { success: true, redirectTo: "/profile" };
      }

      return { success: true, redirectTo: "/" };
    } catch {
      return { success: false };
    }
  },

  logout: async () => {
    try {
      const token = localStorage.getItem("access_token");
      if (token) {
        await axiosInstance.post("auth/logout");
      }
    } catch {
      // Ignore logout errors
    } finally {
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("user");
      localStorage.removeItem("mfa_enrollment_required");
    }

    return {
      success: true,
      redirectTo: "/login",
    };
  },

  check: async () => {
    const token = localStorage.getItem("access_token");

    if (!token) {
      return {
        authenticated: false,
        redirectTo: "/login",
      };
    }

    return {
      authenticated: true,
    };
  },

  getPermissions: async () => {
    const user = localStorage.getItem("user");
    if (!user) return null;

    const userData = JSON.parse(user);
    return userData.roles || [];
  },

  getIdentity: async () => {
    const user = localStorage.getItem("user");
    if (!user) return null;

    const userData = JSON.parse(user);
    return {
      id: userData.id,
      name: userData.userName,
      avatar: undefined,
    };
  },

  onError: async (error) => {
    if (error.response?.status === 401) {
      return {
        logout: true,
        redirectTo: "/login",
        error,
      };
    }

    return { error };
  },
};
